import type { TradingPair } from '../../types/pair';
import type { OHLCV, Timeframe } from '../../types/ohlcv';
import type { Ticker24h } from '../../types/ticker';
import { ProviderError } from '../types';
import { mapCoinbaseCandles, pastWindows, toCoinbaseProductId } from '../coinbase';
import { aggregateToTimeframe, bucketStart } from '../../utils/aggregate';
import { timeframeToMs } from '../../utils/timeframe';
import { ReconnectingSocket } from './ws';
import type { AdapterOptions, ExchangeAdapter } from './types';

/**
 * Coinbase Exchange adapter (public endpoints, no key, CORS `*`).
 *
 * Verified live 2026-10-02:
 *   - GET /products/{BASE-QUOTE}/candles?granularity=S&start=ISO&end=ISO returns
 *     `[time s, low, high, open, close, volume]` rows newest first, both ends
 *     inclusive, at most 300 per call (a wider range is a 400). Granularity must
 *     be 60, 300, 900, 3600, 21600 or 86400 (anything else is a 400); 6h and 1D
 *     buckets open on UTC boundaries. Intervals without trades have no row.
 *   - Candle responses are CDN-cached per URL (`max-age=300`; a repeated URL
 *     came back `cf-cache-status: HIT`), so windows reaching the present end at
 *     the current millisecond. The candles themselves trail the trade feed by
 *     roughly 1–10 s (measured).
 *   - GET /products/stats maps every online product id to
 *     `{ stats_24hour: { open, high, low, last, volume } }`; `open` is the price
 *     24h ago. There is no quote volume, bid/ask or timestamp in it.
 *   - wss://ws-feed.exchange.coinbase.com has no candle channel. `ticker` sends
 *     one frame per trade (price, last_size, time, open_24h, volume_24h,
 *     high_24h, low_24h, best_bid, best_ask) and starts with a repeat of the
 *     last trade before the subscription; `heartbeat` sends one frame per second
 *     per product, so quiet markets do not look stale.
 */

const REST_URL = 'https://api.exchange.coinbase.com';
const WS_URL = 'wss://ws-feed.exchange.coinbase.com';
const MAX_CONCURRENT_REQUESTS = 6;
/** When to seed a live candle again after (re)connecting: REST candles trail trades by up to ~10 s. */
const RESEED_AFTER_MS = 15_000;
/** Most finer candles fetched to roll up one request (5000 days reaches back past Coinbase's 2015 start). */
const MAX_SOURCE_CANDLES = 5000;

/** Native candle sizes, seconds. */
const GRANULARITY: Partial<Record<Timeframe, number>> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '1h': 3600,
  '6h': 21600,
  '1D': 86400,
};

/** Timeframes Coinbase has no granularity for, and the native one they are rolled up from. */
const ROLLUP_SOURCE: Partial<Record<Timeframe, Timeframe>> = {
  '3m': '1m',
  '30m': '15m',
  '2h': '1h',
  '4h': '1h',
  '12h': '6h',
  '3D': '1D',
  '1W': '1D',
  '1M': '1D',
};

/** The native timeframe (and its granularity in seconds) fetched to serve `timeframe`. */
export function coinbaseInterval(timeframe: Timeframe): { source: Timeframe; granularity: number } {
  const source = ROLLUP_SOURCE[timeframe] ?? timeframe;
  const granularity = GRANULARITY[source];
  if (!granularity) throw new ProviderError(`Unsupported timeframe ${timeframe}`);
  return { source, granularity };
}

/** End (exclusive) of the `timeframe` bucket holding `timestamp`; months end on the next 1st. */
export function bucketEnd(timestamp: number, timeframe: Timeframe): number {
  const start = bucketStart(timestamp, timeframe);
  if (timeframe !== '1M') return start + timeframeToMs(timeframe);
  const d = new Date(start);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
}

/** Decimal places of a price/amount step such as "0.01", "0.0000010" or "1.00"; 8 when unknown. */
export function decimalsOfIncrement(increment: string | undefined): number {
  const value = Number(increment);
  if (!increment || !Number.isFinite(value) || value <= 0) return 8;
  return value.toFixed(12).split('.')[1].replace(/0+$/, '').length;
}

/** Runs `task` over `items`, at most `limit` at a time; results keep the input order. */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  let failed = false;
  const worker = async (): Promise<void> => {
    // After a failure the whole call rejects, so starting more requests would be wasted.
    while (!failed && next < items.length) {
      const index = next++;
      try {
        results[index] = await task(items[index]);
      } catch (error) {
        failed = true;
        throw error;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => worker()));
  return results;
}

export interface CoinbaseProduct {
  id: string;
  base_currency: string;
  quote_currency: string;
  quote_increment: string;
  base_increment: string;
  status: string;
  trading_disabled?: boolean;
}

/** `/products` → online, tradable spot pairs sorted by symbol. */
export function mapCoinbaseProducts(products: CoinbaseProduct[]): TradingPair[] {
  return products
    .filter((p) => p.status === 'online' && !p.trading_disabled)
    .map((p) => ({
      symbol: `${p.base_currency}/${p.quote_currency}`,
      base: p.base_currency,
      quote: p.quote_currency,
      exchangeId: 'coinbase',
      priceDecimals: decimalsOfIncrement(p.quote_increment),
      quantityDecimals: decimalsOfIncrement(p.base_increment),
    }))
    .sort((a, b) => a.symbol.localeCompare(b.symbol));
}

export interface CoinbaseProductStats {
  stats_24hour?: { open?: string; high?: string; low?: string; last?: string; volume?: string };
}

/** `/products/stats` → tickers (`timestamp` is when they were fetched); products without a last price are skipped. */
export function mapCoinbaseStats(stats: Record<string, CoinbaseProductStats>, timestamp: number): Ticker24h[] {
  const tickers: Ticker24h[] = [];
  for (const [productId, entry] of Object.entries(stats)) {
    const day = entry?.stats_24hour;
    const last = toNumber(day?.last);
    if (!day || last === undefined || last <= 0) continue;
    const open = toNumber(day.open);
    const volume = toNumber(day.volume);
    tickers.push({
      exchangeId: 'coinbase',
      pair: productId.replace('-', '/'),
      last,
      open24h: open,
      high24h: toNumber(day.high),
      low24h: toNumber(day.low),
      changePercent24h: open ? ((last - open) / open) * 100 : 0,
      baseVolume24h: volume,
      quoteVolume24h: volume === undefined ? undefined : volume * last,
      timestamp,
    });
  }
  return tickers;
}

/** One trade from the `ticker` channel, with the 24h statistics that ride along. */
export interface CoinbaseTrade {
  productId: string;
  price: number;
  size: number;
  /** Unix ms of the trade. */
  time: number;
  ticker: Ticker24h;
}

interface TickerFrame {
  type?: string;
  product_id?: string;
  price?: string;
  last_size?: string;
  time?: string;
  open_24h?: string;
  volume_24h?: string;
  high_24h?: string;
  low_24h?: string;
  best_bid?: string;
  best_ask?: string;
}

/** A feed frame → trade; null for subscription acks, heartbeats, errors and anything unreadable. */
export function parseCoinbaseTicker(raw: string): CoinbaseTrade | null {
  let frame: TickerFrame | null;
  try {
    frame = JSON.parse(raw) as TickerFrame | null;
  } catch {
    return null;
  }
  if (!frame || frame.type !== 'ticker' || typeof frame.product_id !== 'string') return null;
  const price = toNumber(frame.price);
  const time = parseTime(frame.time);
  if (price === undefined || price <= 0 || time === undefined) return null;
  const open = toNumber(frame.open_24h);
  const volume = toNumber(frame.volume_24h);
  return {
    productId: frame.product_id,
    price,
    size: toNumber(frame.last_size) ?? 0,
    time,
    ticker: {
      exchangeId: 'coinbase',
      pair: frame.product_id.replace('-', '/'),
      last: price,
      open24h: open,
      high24h: toNumber(frame.high_24h),
      low24h: toNumber(frame.low_24h),
      changePercent24h: open ? ((price - open) / open) * 100 : 0,
      baseVolume24h: volume,
      quoteVolume24h: volume === undefined ? undefined : volume * price,
      bid: toNumber(frame.best_bid),
      ask: toNumber(frame.best_ask),
      timestamp: time,
    },
  };
}

/**
 * The forming candle of one timeframe, built from trades on top of a REST seed.
 * A seed already holds every trade up to the moment it was requested, so only
 * the volume of trades that arrived after `seedRequested()` is added to it.
 */
export class TradeCandleBuilder {
  private candle: OHLCV | null = null;
  private volumeSinceRequest = 0;
  private closeSinceRequest: number | null = null;

  constructor(private readonly timeframe: Timeframe) {}

  seedRequested(): void {
    this.volumeSinceRequest = 0;
    this.closeSinceRequest = null;
  }

  /** Applies one trade; null when it belongs to a bucket older than the one being built. */
  trade(price: number, size: number, time: number): OHLCV | null {
    const bucket = bucketStart(time, this.timeframe);
    const candle = this.candle;
    if (candle && bucket < candle.timestamp) return null;
    if (!candle || bucket > candle.timestamp) {
      this.candle = { timestamp: bucket, open: price, high: price, low: price, close: price, volume: size };
    } else {
      candle.high = Math.max(candle.high, price);
      candle.low = Math.min(candle.low, price);
      candle.close = price;
      candle.volume += size;
    }
    this.volumeSinceRequest += size;
    this.closeSinceRequest = price;
    return this.current();
  }

  /**
   * Merges the REST candle of the bucket in progress (undefined when it had no trades yet).
   * `keepClose`: the feed stayed connected since the last seed, so its close is newer than REST's.
   */
  seed(seed: OHLCV | undefined, keepClose = false): OHLCV | null {
    const live = this.candle;
    if (seed && (!live || seed.timestamp > live.timestamp)) {
      this.candle = { ...seed };
    } else if (seed && live && seed.timestamp === live.timestamp) {
      this.candle = {
        timestamp: seed.timestamp,
        open: seed.open,
        high: Math.max(seed.high, live.high),
        low: Math.min(seed.low, live.low),
        close: keepClose ? live.close : (this.closeSinceRequest ?? seed.close),
        // Both sums can only fall short (REST lags, the feed starts late), so the larger is closer.
        volume: Math.max(live.volume, seed.volume + this.volumeSinceRequest),
      };
    }
    // An older seed means the bucket rolled over while it was in flight; the live candle stands.
    return this.current();
  }

  current(): OHLCV | null {
    return this.candle ? { ...this.candle } : null;
  }
}

export function coinbaseAdapter(options: AdapterOptions = {}): ExchangeAdapter {
  async function getJson<T>(path: string): Promise<T> {
    const doFetch = options.fetch ?? globalThis.fetch;
    const response = await doFetch(`${REST_URL}${path}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message =
        body && typeof body === 'object' && typeof (body as { message?: unknown }).message === 'string'
          ? (body as { message: string }).message
          : `Coinbase request failed (${response.status})`;
      throw new ProviderError(message, response.status, body);
    }
    return (await response.json()) as T;
  }

  /** Native candles in [from, to], paged under the 300-row cap, oldest first. */
  async function fetchCandles(productId: string, granularity: number, from: number, to: number): Promise<OHLCV[]> {
    // A window reaching the present ends now, to the ms: a repeated URL would be served from the CDN cache.
    const windows = pastWindows(from, Math.min(to, Date.now()), granularity * 1000);
    const pages = await mapWithConcurrency(windows, MAX_CONCURRENT_REQUESTS, ({ start, end }) => {
      const query = new URLSearchParams({
        granularity: String(granularity),
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
      });
      return getJson<unknown>(`/products/${productId}/candles?${query}`).then(mapCoinbaseCandles);
    });
    const merged = new Map<number, OHLCV>();
    for (const page of pages) for (const candle of page) merged.set(candle.timestamp, candle);
    return [...merged.values()].sort((a, b) => a.timestamp - b.timestamp);
  }

  const adapter: ExchangeAdapter = {
    id: 'coinbase',
    name: 'Coinbase',

    async getPairs(): Promise<TradingPair[]> {
      return mapCoinbaseProducts(await getJson<CoinbaseProduct[]>('/products'));
    },

    async getOHLCV({ pair, timeframe, from, to }): Promise<OHLCV[]> {
      const productId = toCoinbaseProductId(pair);
      const { source, granularity } = coinbaseInterval(timeframe);
      const inWindow = (candle: OHLCV) => candle.timestamp >= from && candle.timestamp <= to;
      if (source === timeframe) return (await fetchCandles(productId, granularity, from, to)).filter(inWindow);
      // Start on a bucket boundary so the first rolled-up candle is complete, and cap the finer fetch.
      const start = Math.max(
        bucketStart(from, timeframe),
        bucketStart(to - MAX_SOURCE_CANDLES * granularity * 1000, timeframe),
      );
      // Like a native candle, the bucket holding `to` comes back whole, so fetch to its end.
      const fine = await fetchCandles(productId, granularity, start, bucketEnd(to, timeframe) - 1);
      return aggregateToTimeframe(fine, timeframe).filter(inWindow);
    },

    async getTickers(): Promise<Ticker24h[]> {
      return mapCoinbaseStats(await getJson<Record<string, CoinbaseProductStats>>('/products/stats'), Date.now());
    },

    subscribeOHLCV(pair, timeframe, onCandle, onStatus) {
      const productId = toCoinbaseProductId(pair);
      const builder = new TradeCandleBuilder(timeframe);
      let stopped = false;
      // Until the first seed lands the candle only holds trades seen live, so its open would be wrong.
      let seeded = false;
      let replayPending = false;
      let connections = 0;
      let followUp: ReturnType<typeof setTimeout> | null = null;

      const emit = (candle: OHLCV | null) => {
        if (candle && seeded && !stopped) onCandle(candle);
      };
      // REST candles trail the trade feed by ~1–10 s, so trades in that gap before the subscription
      // are in neither source: the bucket in progress at (re)connect can miss a few seconds of
      // volume. A seed taken early in a bucket can even lack its first trades (the open), hence
      // the second seed once REST has caught up. Later buckets are built from every trade.
      const seed = (keepClose: boolean) => {
        const now = Date.now();
        builder.seedRequested();
        adapter.getOHLCV({ pair, timeframe, from: bucketStart(now, timeframe), to: now }).then(
          (candles) => {
            if (stopped) return;
            seeded = true;
            emit(builder.seed(candles[candles.length - 1], keepClose));
          },
          () => {
            if (stopped) return;
            // Carry on with the trades seen live; the bucket in progress may lack its earlier part.
            seeded = true;
            onStatus?.('error');
            emit(builder.current());
          },
        );
      };
      const seedTwice = () => {
        seed(false);
        if (followUp) clearTimeout(followUp);
        followUp = setTimeout(() => seed(true), RESEED_AFTER_MS);
      };

      seedTwice();
      const socket = new ReconnectingSocket({
        urls: [WS_URL],
        WebSocket: options.WebSocket,
        onStatus,
        onOpen: (send) => {
          // Trades missed while disconnected can only come back through REST.
          if (connections++ > 0) seedTwice();
          replayPending = true;
          send(JSON.stringify({ type: 'subscribe', product_ids: [productId], channels: ['ticker', 'heartbeat'] }));
        },
        onMessage: (raw) => {
          const trade = parseCoinbaseTicker(raw);
          if (!trade || trade.productId !== productId) return;
          if (replayPending) {
            // The first ticker after subscribing repeats a trade the seed already holds.
            replayPending = false;
            return;
          }
          emit(builder.trade(trade.price, trade.size, trade.time));
        },
      });
      return () => {
        stopped = true;
        if (followUp) clearTimeout(followUp);
        socket.close();
      };
    },

    subscribeTickers(pairs, onTicker) {
      const productIds = pairs.map(toCoinbaseProductId);
      if (productIds.length === 0) return () => {};
      const socket = new ReconnectingSocket({
        urls: [WS_URL],
        WebSocket: options.WebSocket,
        onOpen: (send) =>
          send(
            JSON.stringify({
              type: 'subscribe',
              product_ids: productIds,
              // One heartbeat stream keeps the socket from being recycled when every market is quiet.
              channels: ['ticker', { name: 'heartbeat', product_ids: productIds.slice(0, 1) }],
            }),
          ),
        onMessage: (raw) => {
          const trade = parseCoinbaseTicker(raw);
          if (trade) onTicker(trade.ticker);
        },
      });
      return () => socket.close();
    },
  };
  return adapter;
}

/** Finite number from a venue's string or number field; undefined when absent or unreadable. */
function toNumber(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number.parseFloat(value) : Number.NaN;
  return Number.isFinite(n) ? n : undefined;
}

/** ISO time with any number of fraction digits (Coinbase sends microseconds) → Unix ms. */
function parseTime(iso: string | undefined): number | undefined {
  if (typeof iso !== 'string') return undefined;
  const ms = Date.parse(iso.replace(/(\.\d{3})\d+/, '$1'));
  return Number.isFinite(ms) ? ms : undefined;
}
